// Brand + legal strings. Single source of truth for anything that
// names the company, the product, the support inbox, or the canonical
// URLs. Edit this file → app-wide propagation.
//
// Convention:
//   - Trade names ("Yeenon Games", "BattleShark") are surfaced to users.
//   - Legal references use the LLC: copyright notices, contracts,
//     privacy-policy contact blocks.
//
// Why a config module and not env vars: these strings are non-secret,
// rarely change, and need to be deterministic at build time (no runtime
// surprise if an env var is missing during App Store review).

export const APP_NAME = 'BattleShark' as const;
export const TRADE_NAME = 'Yeenon Games' as const;
export const LEGAL_ENTITY = 'Yeenon, LLC' as const;

/** Public support inbox. Used in mailto: links + privacy-policy contact. */
export const SUPPORT_EMAIL = 'contact@battleshark.online' as const;

/**
 * Transactional sender address for game emails delivered via Resend
 * (turn reminders, account-deletion receipts, etc.). Matches the
 * Supabase-template sender so all BattleShark email arrives from one
 * place. Replies route to SUPPORT_EMAIL.
 */
export const NOREPLY_EMAIL = 'noreply@battleshark.online' as const;
export const NOREPLY_SENDER = `BattleShark <${NOREPLY_EMAIL}>` as const;

/**
 * Canonical URLs. Legal pages live on the game's own domain
 * (battleshark.online) as static HTML in `public/`, served by Vercel.
 * Game ships before the parent-company site, so the privacy/terms/
 * contact pages are co-hosted with the app. When yeenon.com
 * eventually launches, these URLs can either redirect there or stay
 * as per-product policy pages — many studios keep both.
 */
export const PARENT_WEBSITE = 'https://yeenon.com' as const;
export const PRIVACY_URL = 'https://battleshark.online/privacy' as const;
export const TERMS_URL = 'https://battleshark.online/terms' as const;
export const CONTACT_URL = 'https://battleshark.online/contact' as const;

/**
 * Community Discord invite. Surfaced in the About page footer above
 * the support email so testers can drop into the live channel for
 * quick questions / bug chatter without composing an email.
 */
export const DISCORD_URL = 'https://discord.gg/VXxuKSSvk' as const;

/**
 * Community subreddit. Surfaced next to the Discord link in the
 * About page footer — slower-paced async community for screenshots,
 * patch-note discussion, and longer-form feedback.
 */
export const REDDIT_URL =
  'https://www.reddit.com/r/BattleSharkOnline/' as const;

/**
 * Whether the in-app About-page footer surfaces the Privacy + Terms
 * link row. While `false`, the row is hidden so beta testers don't
 * tap into 404s. Support email + copyright still surface either way.
 *
 * Apple App Submission requires both URLs to load — this flag MUST
 * be true before the iOS build is submitted for review.
 *
 * Flipped to `true` on 2026-05-17 when the static legal pages landed
 * in `public/` (privacy.html, terms.html, contact.html).
 */
export const LEGAL_PAGES_LIVE = true as const;

/**
 * Registered agent address (Northwest Registered Agent, Denver CO).
 * Used in privacy-policy contact blocks and any other place that
 * surfaces a postal contact. This is the LLC's legal-service address;
 * not a business operating address.
 */
export const CONTACT_ADDRESS = {
  line1: '1500 N Grant St',
  line2: 'Ste N',
  city: 'Denver',
  county: 'Denver County',
  state: 'CO',
  zip: '80203',
  country: 'US',
} as const;

/**
 * Copyright year. Computed at module load so the footer flips on
 * Jan 1 without a code change. Floored at 2026 (entity formation
 * year) in case the device clock is set in the past.
 */
export const COPYRIGHT_YEAR: number = Math.max(2026, new Date().getFullYear());

/** Pre-formatted footer string. */
export const COPYRIGHT_NOTICE: string =
  `© ${COPYRIGHT_YEAR} ${LEGAL_ENTITY}. All rights reserved.`;
